import { Autocomplete, Button, FormControlLabel, Stack, Switch } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import countries from "i18n-iso-countries";
import countriesEn from "i18n-iso-countries/langs/en.json";
import { useNotification } from "providers/Notification";
import { SearchableSelectOption, collapsibleAutocompleteGroupKey, findOption, findOptions, groupedSectionSlotProps, isChipKeyboardRemoval, optionValuesEqual, optionsFromDict, renderOptionChips, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically, useCollapsibleAutocompleteGroups } from "utils/searchableOptions";
import { ListInput } from "components/ListInput";
import clientDevices from "../../../../constant/clientDevices";
import contentTypes from "../../../../constant/contentTypes";
import { AlertGroupOption, withSelectedAlertGroups } from "./groupOptions";

countries.registerLocale(countriesEn);


const unavailableDirectoryGroupKey = collapsibleAutocompleteGroupKey(
    "unavailable-directory",
    "Unavailable directory",
);

function alertGroupChipLabel(option: SearchableSelectOption<string>): string {
    const group = option as Partial<AlertGroupOption>;
    return group.directoryId
        ? `${option.label} (${group.directoryName || "Unavailable directory"})`
        : option.label;
}

interface AddConditionProps {
    initialValue?: AlertCondition
    groups: AlertGroupOption[]
    rules: { [key: string]: string }
    dataTypes: { [key: string]: string }
    applications: { [key: string]: string }
    activityFields: { [key: string]: string }
    onBackButtonClicked?: () => void
    //onExit is called when the user wants to go back without changing anything
    onCancel?: () => void
    onFilterAdded: (filter: AlertCondition, reset: () => void) => void
}

export interface AlertCondition {
    index?: number
    type: string
    values: string[]
    isNot?: boolean
}

export default function AddCondition(props: AddConditionProps) {
    const notification = useNotification();
    const [type, setType] = useState("");
    const [values, setValues] = useState([] as string[]);
    const [isNot, setIsNot] = useState(false);

    const [validationError, setValidationError] = useState(false);
    const [editing, setEditing] = useState(false);
    const collapsibleGroups = useCollapsibleAutocompleteGroups();


    const getListFromType = (): { [key: string]: string } => {
        switch (type) {
        case "apps":
            return props.applications;
        case "rules":
            return props.rules;
        case "datatypes_detected":
        case "datatypes_obfuscated":
            return props.dataTypes;
        case "client_ip_location":
            return Object.entries(countries.getAlpha2Codes()).reduce((data, [code]) => {
                data[code] = countries.getName(code, "en") || "";
                return data;
            }, {} as { [key: string]: string });
        case "client_device":
            return clientDevices.reduce((data, item) => {
                data[item] = item;
                return data;
            }, {} as { [key: string]: string });
        case "content_type":
            return contentTypes.sort().reduce((data, item) => {
                data[item] = item;
                return data;
            }, {} as { [key: string]: string });
        default:
            return {};
        }
    };

    const reset = () => {
        setType("");
        setValues([]);
        setIsNot(false);

        setEditing(false);
    };

    useEffect(() => {
        if (!props.initialValue) {
            reset();
            return;
        }

        setType(props.initialValue.type);
        setValues(props.initialValue.values || []);
        setIsNot(props.initialValue.isNot || false);

        setEditing(true);
    }, [props.initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = () => {
        if (type === "") {
            notification.Display({
                type: "error",
                title: "Invalid parameters",
                message: "Filter type cannot be empty"
            });
            setValidationError(true);
            return;
        }

        props.onFilterAdded({
            index: props.initialValue?.index,
            type: type,
            values: values,
            isNot: isNot,
        }, reset);
    };

    const typeOptions = sortOptionsAlphabetically(optionsFromDict(props.activityFields));
    const userGroupValueOptions = useMemo(
        () => withSelectedAlertGroups(props.groups, values),
        [props.groups, values],
    );
    const valueOptions = type === "usergroup"
        ? userGroupValueOptions
        : sortOptionsAlphabetically(optionsFromDict(getListFromType()));
    const selectedValueOptions = useMemo(
        () => findOptions(valueOptions, values),
        [valueOptions, values],
    );

    return <Stack sx={{ paddingTop: "10px" }}>
        {!editing ? <>
            <Button
                id="add-button"
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => {
                    reset();
                    setEditing(true);
                }}
            >
                Add
            </Button>
        </> : <>
            <Autocomplete
                fullWidth
                disableClearable={true as boolean}
                sx={{ marginTop: "20px" }}
                id="filter-type-select"
                options={typeOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={optionValuesEqual}
                value={findOption(typeOptions, type || null)}
                onChange={(_, option) => {
                    setType(option?.value || "");
                    setValues([]);
                }}
                renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(typeOptions, type || null)?.label)}
                renderInput={(params) => searchTextField(params, { label: "Type", error: validationError })}
            />

            <FormControlLabel
                sx={{ marginTop: "10px" }}
                control={
                    <Switch
                        id="not-condition-switch"
                        checked={isNot}
                        onChange={(e) => setIsNot(e.target.checked)}
                        color="primary"
                    />
                }
                label="Exclude (NOT)"
            />

            {["client_ip", "data_values", "username"].includes(type) ?
                <ListInput id="text-list-input"
                    label={props.activityFields[type]}
                    placeholder="Press ENTER to confirm"
                    value={values}
                    delimiters={[]}
                    onDelete={x => setValues(values.filter(y => y !== x))}
                    onChange={x => setValues(x)}
                    chipColor={isNot ? "error" : "primary"}
                />
                :
                type && <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disableClearable
                    fullWidth
                    sx={{ marginTop: "20px" }}
                    id="values-select"
                    options={valueOptions}
                    open={collapsibleGroups.open}
                    onOpen={collapsibleGroups.onOpen}
                    onClose={collapsibleGroups.onClose}
                    groupBy={type === "usergroup"
                        ? (option) => {
                            const group = option as Partial<AlertGroupOption>;
                            if (!group.directoryId) {
                                return unavailableDirectoryGroupKey;
                            }
                            const directoryName = group.directoryName || "Unavailable directory";
                            return collapsibleAutocompleteGroupKey(group.directoryId, directoryName);
                        }
                        : undefined}
                    renderGroup={type === "usergroup" ? collapsibleGroups.renderGroup : undefined}
                    onInputChange={type === "usergroup" ? collapsibleGroups.onInputChange : undefined}
                    slotProps={type === "usergroup" ? groupedSectionSlotProps : undefined}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={optionValuesEqual}
                    value={selectedValueOptions}
                    onChange={(event, options, reason) => {
                        if (isChipKeyboardRemoval(event, reason)) {
                            return;
                        }
                        setValues(options.map(x => x.value));
                    }}
                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, values.some(v => v === option.value))}
                    renderTags={(value, getTagProps) => renderOptionChips(
                        value,
                        getTagProps,
                        isNot ? "error" : "primary",
                        type === "usergroup" ? alertGroupChipLabel : undefined,
                    )}
                    renderInput={(params) => searchTextField(params, {
                        label: props.activityFields[type],
                        groupToggleInputBlur: type === "usergroup"
                            ? collapsibleGroups.onInputBlurToGroupToggle
                            : undefined,
                    })}
                />
            }

            <Box display="flex" flexGrow={1} alignItems={"end"} marginBottom="15px" marginTop="20px">
                <Button
                    id="cancel-button"
                    variant="text"
                    size="medium"
                    fullWidth
                    onClick={() => {
                        reset();
                        props.onCancel && props.onCancel();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    id="save-button"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    onClick={save}
                >
                    Save
                </Button>
            </Box>
        </>}

    </Stack>;
}
